import React from 'react';
import { values, path, compose } from 'lodash/fp';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import map from 'lodash/map';
import get from 'lodash/get';
import merge from 'lodash/merge';
import { withStyles } from '@glu/theming';
import { withFormProvider } from '@glu/validation-react';
import { withRouter } from 'react-router-dom';
import classnames from 'classnames';
import memoize from 'lodash/memoize';
import styles from './createPaymentForm.styles';
import AppPropTypes from '../../proptypes/AppPropTypes';
import withFormPrompt from '../withFormPrompt/withFormPrompt';

// components
import Alert from '../alert/Alert';
import CreatePaymentFooter from './CreatePaymentFooter';
import PaymentType from '../wirePayment/components/PaymentType';
import SpinResolver from '../spinResolver/SpinResolver';
import withLocale from '../withLocale/withLocale';
import withSubscription from '../withSubscriptions/withSubscriptions';

// utils
import { applyToContext } from './CreatePayment.utils';
import { buildDropdownOptions } from './CreatePayment.dropdowns';
import { buildRetrievePaymentData } from './createPayment.api';

// api
import {
  getPaymentTypes,
  getWirePreferences,
  getValidBusinessDate,
  getWireAccounts,
  getFxRates
} from '../../api/payments/service';

export class CreatePaymentFormComponent extends React.Component {
  state = {
    error: {
      message: null,
      thrown: false
    },
    form: {
      paymentType: undefined
    },
    loading: true
  };

  sources = {};

  /**
   * Invoked when cancel button is clicked
   * @returns {void}
   */
  onCancel = () => {
    const { history, onDiscard } = this.props;
    if (!onDiscard) {
      return null;
    }

    const { onAction, returnUrl } = onDiscard;
    if (onAction) {
      onAction();
    }
    return history.push({ pathname: returnUrl });
  };

  /**
   * Method is invoked, when the user is doing the submission.
   * @param {SyntheticEvent} eventoad
   * @returns {void}
   */
  onSubmit = (event) => {
    const { action, data: formState, errors, footer, setIsSubmitting } = this.props;
    event.preventDefault();
    event.stopPropagation();

    const failing = compose(
      (arr) => arr.some(Boolean),
      (arr) => arr.filter((x) => x?.length > 0),
      (arr) => arr.filter((x) => typeof x !== 'undefined'),
      values,
      path('errors')
    )(errors);

    if (failing) {
      setIsSubmitting(false);
      return false;
    }
    setIsSubmitting(true);
    return footer.submit.fn({
      fields: formState,
      action,
      remainingApprovals: this.props.remainingApprovals,
      options: {
        paymentTypeOptions: this.paymentTypeOptions,
        amountTypes: this.amountTypes,
        fxRates: this.fxRates,
        bankCodes: this.bankCodes,
        paymentAccounts: this.paymentAccounts,
        preferences: this.wirePreferences,
        ...this.sources
      }
    });
  };

  /**
   * Attach services to current context.
   *
   * @param {object} services
   * @returns {void}
   */
  onLoad = (services, reload, shouldMerge = true) => {
    Object.keys(services).forEach((service) => {
      this.sources[service] =
        this.sources[service] && shouldMerge
          ? merge(this.sources[service], services[service])
          : services[service];
    });

    if (this.state.loading || reload) {
      this.setState({ loading: false });
    }
  };

  onHide = () => {
    this.props.clearError();
  };

  getWirePaymentAccounts = memoize(() => {
    const { action, fromTemplate } = this.props;
    return getWireAccounts({
      includeTemplateOnlyAccounts: fromTemplate || action.includes('template')
    });
  });

  render() {
    const {
      FooterExtraBtns,
      action,
      classes: { apiError, formEl, greyBg, greyContent },
      data: formState,
      disableFooterOnSubmission,
      error,
      footer,
      fromTemplate,
      isSubmitting,
      locale,
      nocontent,
      onChange: formChange,
      onParentUpdate,
      preSelect,
      preloadFields,
      preloaded,
      setIsSubmitting,
      submissionDisabled,
      topSectionContent,
      type,
      validate: onValidate
    } = this.props;

    /**
     * Payment types need to be reordered according to a static map
     * @param {object} options list of payment types returned by api
     * @returns {object} reordered list of payment types
     */
    const reorderPaymentTypes = (paymentTypes) => {
      const paymentTypesObj = {};
      const paymentTypeKeys = ['Wire', 'ACH', 'Tax', 'Loan'];
      paymentTypeKeys.forEach((paymentType) => {
        const currentTypeArray = paymentTypes[paymentType];
        // include paymentType only if it has children
        if (currentTypeArray && currentTypeArray.length > 0) {
          // get descriptions from payment type and order alphabetically
          const descriptions = map(currentTypeArray, 'description').sort();
          // loop through payment type descriptions and retrieve associated data
          const items = descriptions.map((description) =>
            currentTypeArray.find((currentType) => get(currentType, 'description') === description)
          );
          // assign to new payment types object and reverse order if current payment type is Loan
          paymentTypesObj[paymentType] = paymentType !== 'Loan' ? items : items.reverse();
        }
      });
      return paymentTypesObj;
    };
    return (
      <section>
        <SpinResolver
          services={[
            getPaymentTypes,
            getWirePreferences,
            this.getWirePaymentAccounts,
            getValidBusinessDate,
            getFxRates
          ]}
        >
          {({
            results: [paymentTypesAll, wirePreferences, paymentAccounts, validDate, fxRates]
          }) => {
            const paymentTypes = {
              ...paymentTypesAll,
              ACH:
                paymentTypesAll.ACH && paymentTypesAll.ACH.filter((type) => type.code !== 'EMP+CO')
            };
            const paymentTypesObj = reorderPaymentTypes(paymentTypes);
            // remove item with no children
            const options = buildDropdownOptions(
              { paymentTypesObj, fxRates },
              {
                validDate,
                wirePreferences,
                paymentAccounts
              }
            );

            applyToContext(this, options);

            if (!Object.keys(formState).length) {
              if (preloadFields) {
                const data = buildRetrievePaymentData(preloadFields, options, locale);
                Object.keys(data).forEach((k) => {
                  const instance = data[k];
                  // eslint-disable-next-line prefer-template
                  const hasValue =
                    typeof instance === 'number' ||
                    (instance && Object.values(instance).filter(Boolean).length);
                  Object.assign(formState, { [k]: (hasValue && instance) || null });
                });

                formChange('paymentType', formState.paymentType);

                return null;
              }
            }

            return (
              <form className={formEl}>
                {error.thrown && (
                  <div className={classnames(apiError, 'col-xs-12')}>
                    {error.thrown && (
                      <Alert onHide={this.onHide} variant="warning" errorGuid={error.errorGuid}>
                        {error.message}
                      </Alert>
                    )}
                  </div>
                )}
                {!nocontent && (
                  <fieldset className={greyBg} disabled={submissionDisabled}>
                    <legend className="visuallyHidden">{locale['create.payment.title']}</legend>

                    <div className={classnames('no-gutters col-xs-12', greyContent)}>
                      <div className="create-payment__top-section">
                        <div className="create-payment__top-section__rows">
                          <PaymentType
                            formChange={formChange}
                            options={this.paymentTypeOptions}
                            formState={preloadFields || formState}
                            preloadFields={preloadFields}
                            preloaded={fromTemplate || preloaded}
                            preSelect={preSelect}
                          />
                          {topSectionContent &&
                            topSectionContent({ options, formChange, formState })}
                        </div>
                      </div>
                    </div>
                  </fieldset>
                )}

                <fieldset disabled={submissionDisabled}>
                  {this.props.children({
                    form: {
                      ...formState,
                      ...preloadFields
                    },
                    formState,
                    validate: onValidate,
                    type,
                    action,
                    events: {
                      onParentUpdate,
                      formChange,
                      onLoad: this.onLoad
                    },
                    fromTemplate,
                    preloaded,
                    data: {
                      validDate,
                      paymentTypeOptions: this.paymentTypeOptions,
                      amountTypes: this.amountTypes,
                      paymentAccounts,
                      wirePreferences,
                      fxRates: this.fxRates
                    },
                    services: this.sources
                  })}
                </fieldset>

                <CreatePaymentFooter
                  type={type}
                  action={action}
                  fields={formState}
                  options={options}
                  append={footer.append}
                  disableOnLoad={footer.disableOnLoad}
                  setLoading={
                    this.props.footer.submit.isInvalid ||
                    this.state.loading ||
                    submissionDisabled ||
                    disableFooterOnSubmission
                  }
                  submit={{ fn: this.onSubmit, label: footer.submit.label }}
                  validate={{ fn: onValidate }}
                  cancel={{ fn: this.onCancel }}
                  showTotal={footer.showTotal}
                  submissionDisabled={submissionDisabled || disableFooterOnSubmission}
                  preloadFields={preloadFields}
                  setIsSubmitting={setIsSubmitting}
                  isSubmitting={isSubmitting}
                >
                  {(props) => <FooterExtraBtns {...props} />}
                </CreatePaymentFooter>
              </form>
            );
          }}
        </SpinResolver>
      </section>
    );
  }
}

CreatePaymentFormComponent.defaultProps = {
  locale: {},
  classes: {},
  clearError() {},
  onDiscard: null,
  onAmountChange() {},
  validate() {},
  history: {},
  type: '',
  errors: {},
  children() {},
  error: {},
  footer: {
    submit: {},
    showTotal: true
  },
  preloadFields: null,
  action: '',
  nocontent: false,
  FooterExtraBtns: () => null,
  data: {},
  onChange() {},
  fromTemplate: false,
  submissionDisabled: false,
  disableFooterOnSubmission: false,
  setIsSubmitting() {},
  isSubmitting: false,
  topSectionContent: null,
  preloaded: false,
  onParentUpdate() {},
  preSelect: []
};

CreatePaymentFormComponent.propTypes = {
  classes: AppPropTypes.jssClasses,
  locale: AppPropTypes.locale,
  clearError: PropTypes.func,
  action: PropTypes.string,
  nocontent: PropTypes.bool,
  onDiscard: PropTypes.shape({
    onAction: PropTypes.func,
    onCallback: PropTypes.func,
    returnUrl: PropTypes.string
  }),
  validate: PropTypes.func,
  onAmountChange: PropTypes.func,
  history: PropTypes.shape({
    push: PropTypes.func
  }),
  type: PropTypes.string,
  errors: PropTypes.shape({}),
  error: PropTypes.shape({}),
  footer: PropTypes.shape({
    submit: PropTypes.shape({
      isInvalid: PropTypes.bool
    })
  }),
  children: PropTypes.func,
  preloadFields: PropTypes.shape({}),
  FooterExtraBtns: PropTypes.func,
  data: PropTypes.shape({}),
  onChange: PropTypes.func,
  fromTemplate: PropTypes.bool,
  theme: PropTypes.shape({}).isRequired,
  submissionDisabled: PropTypes.bool,
  disableFooterOnSubmission: PropTypes.bool,
  setIsSubmitting: PropTypes.func,
  isSubmitting: PropTypes.bool,
  topSectionContent: PropTypes.func,
  onParentUpdate: PropTypes.func,
  remainingApprovals: PropTypes.number.isRequired,
  preloaded: PropTypes.bool,
  preSelect: PropTypes.arrayOf(PropTypes.shape({}))
};

const Prompt = withFormPrompt()(CreatePaymentFormComponent);
const Validations = withFormProvider(Prompt);
const Styled = withStyles(styles)(Validations);
const Localized = withLocale(Styled);
const Routed = withRouter(Localized);
const Subscribed = connect(
  (state) => ({ remainingApprovals: get(state, 'payments.remainingApprovals', 0) }),
  null
)(withSubscription(Routed));

export default ({ ...props }) => <Subscribed {...props} initialData={{}} />;
